import type React from 'react';
import { useEffect, useRef, useState } from 'react';

interface UseNumericInputOptions {
  /** The numeric value from the parent (form state). undefined = empty. */
  value: number | undefined;
  /** Called when the user types a complete valid number, or undefined when cleared. */
  onChange: (value: number | undefined) => void;
  /** Validates whether a raw string is acceptable as partial input (e.g., allows "-", "5."). */
  isValidInput: (raw: string) => boolean;
  /** Parses a raw string into a number, returning undefined for incomplete/invalid input. */
  parse: (raw: string) => number | undefined;
  /** Formats a number for display (e.g., max 2 decimals, no trailing zeros). */
  format: (value: number) => string;
}

interface UseNumericInputReturn {
  /** The string to display in the TextField */
  inputValue: string;
  /** onChange handler for the TextField */
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** onBlur handler for the TextField */
  handleBlur: () => void;
}

export function useNumericInput({
  value,
  onChange,
  isValidInput,
  parse,
  format,
}: UseNumericInputOptions): UseNumericInputReturn {
  const [inputValue, setInputValue] = useState<string>(() =>
    value !== undefined ? format(value) : '',
  );
  const inputValueRef = useRef(inputValue);

  useEffect(() => {
    const currentParsed = parse(inputValueRef.current);

    if (value !== currentParsed) {
      if (value !== undefined) {
        const newValue = format(value);
        setInputValue(newValue);
        inputValueRef.current = newValue;
      } else {
        setInputValue('');
        inputValueRef.current = '';
      }
    }
  }, [value, parse, format]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    if (!isValidInput(rawValue)) {
      return;
    }

    setInputValue(rawValue);
    inputValueRef.current = rawValue;

    const parsed = parse(rawValue);
    onChange(parsed);
  };

  const handleBlur = () => {
    if (value !== undefined) {
      const formatted = format(value);
      setInputValue(formatted);
      inputValueRef.current = formatted;
    } else if (inputValueRef.current !== '') {
      setInputValue('');
      inputValueRef.current = '';
    }
  };

  return { inputValue, handleChange, handleBlur };
}
